// src/modules/operaciones/inventarios/components/ComponentesPrecios.js
//
// Componentes visuales pequeños del módulo "Ajustar Precios": BadgeTipo, Kpi, Spinner, ThOrden y TarjetaAlerta.
import React from 'react'; // Importa React (React.memo)
import { fmt } from '../../../../utils/format'; // Formatea números (separador de miles)
import { BADGES, colorSaldo } from '../utils/preciosComun'; // Apariencia de los badges y color del saldo

/* ---------- Componentes auxiliares (fuera del principal para no recrearse en cada render) ---------- */

// Etiqueta (badge) del tipo de alerta; muestra "-" si el tipo no es reconocido
export const BadgeTipo = React.memo(function BadgeTipo({ tipoAlerta, claseSub }) {
  const b = BADGES[tipoAlerta]; // Configuración del badge para este tipo
  if (!b) return <span className={`text-xs ${claseSub}`}>-</span>;
  return (
    <span title={b.ayuda} className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border whitespace-nowrap ${b.clase}`}>
      {b.label}
    </span>
  );
});

// Tarjeta de indicador (KPI): título y valor; muestra "–" mientras carga
export const Kpi = React.memo(function Kpi({ titulo, valor, color, loading, claseCard, claseSub }) {
  return (
    <div className={`${claseCard} rounded-2xl px-5 py-4 shadow-sm`}>
      <p className={`text-xs uppercase tracking-wider font-bold ${claseSub}`}>{titulo}</p>
      <p className={`text-3xl font-extrabold font-mono mt-1 ${color}`}>{loading ? '–' : fmt(valor)}</p>
    </div>
  );
});

// Indicador de carga giratorio (accesible con role="status")
export const Spinner = React.memo(function Spinner() {
  return <span role="status" aria-label="Cargando" className="inline-block w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />;
});

// Encabezado de columna ordenable. Props: campo = clave del dato, label = texto, align = clase de alineación,
// orden = { campo, dir } actual, onOrden = callback al hacer clic
export const ThOrden = React.memo(function ThOrden({ campo, label, align, orden, onOrden }) {
  const activo = orden.campo === campo; // ¿Esta columna es la ordenada?
  const flecha = !activo ? '↕' : orden.dir === 'asc' ? '▲' : '▼'; // Indicador visual del orden
  return (
    <th className={`px-6 py-4 font-bold ${align}`} aria-sort={activo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onOrden(campo)} className="inline-flex items-center gap-1 uppercase tracking-wider font-bold hover:opacity-80 cursor-pointer">
        {label}<span className="text-[10px] opacity-70">{flecha}</span>
      </button>
    </th>
  );
});

// Tarjeta de una alerta para la vista móvil. Props: a = artículo, tema = paleta de clases, onDetalle = abre el detalle
export const TarjetaAlerta = React.memo(function TarjetaAlerta({ a, tema, onDetalle }) {
  // Bloque título/valor reutilizado por los tres datos numéricos
  const dato = (titulo, valor, clase) => (
    <div>
      <p className={tema.textSub}>{titulo}</p>
      <p className={clase}>{valor}</p>
    </div>
  );
  return (
    <div className={`${tema.bgCard} border ${tema.borderMain} rounded-2xl p-4 shadow-sm flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-purple-500 font-bold text-sm">{a.CODIGO}</p>
          <p className={`text-sm ${tema.textMain} break-words`}>{a.NOMBRE}</p>
        </div>
        <BadgeTipo tipoAlerta={a.TIPO_ALERTA} claseSub={tema.textSub} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        {dato('Existencia', fmt(a.EXISTENCIA_TOTAL), `font-mono font-bold text-base ${colorSaldo(a.EXISTENCIA_TOTAL)}`)}
        {dato('Precio', `$ ${fmt(a.PRECIO_ACTUAL)}`, 'font-mono text-red-500')}
        {dato('Costo prom.', `$ ${fmt(a.COSTO_PROMEDIO)}`, 'font-mono text-red-500')}
      </div>
      <button
        onClick={() => onDetalle(a)}
        className={`w-full px-3 py-2 rounded-lg text-xs font-bold border ${tema.borderInput} ${tema.bgBtn} ${tema.textMain} hover:opacity-80 transition-colors cursor-pointer`}
      >
        Detalles
      </button>
    </div>
  );
});